// src/components/lead-tracker/LeadTrackerTable.jsx
// The month sheet: one row per lead, all derived. Chip click flips Call/Appt
// (a saved override, pencil-marked); Referred By cell opens the picker; row
// click deep-links to the lead in its pipeline (?lead=<id>).
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { rowsForMonth, UNASSIGNED_BUCKET } from "../../utils/leadTracker";
import { setContactReferrer } from "../../utils/firebasefunctions";
import { formatDate } from "../../utils/helper";
import ReferredByPicker from "./ReferredByPicker";

export default function LeadTrackerTable({
  rows,
  year,
  monthIndex,
  bucketFilter,
  onFlipCallAppt,
  onReferrerSaved,
}) {
  const navigate = useNavigate();
  const [pickerRow, setPickerRow] = useState(null);
  const monthRows = useMemo(() => {
    const base = rowsForMonth(rows, year, monthIndex);
    return bucketFilter ? base.filter((r) => r.bucket === bucketFilter) : base;
  }, [rows, year, monthIndex, bucketFilter]);

  const openLead = (row) => {
    navigate(
      `/dashboard/${row.leadType === "buyer" ? "buyers" : "listings"}?lead=${row.leadId}`,
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[840px]">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left">
              {["Date", "Name", "B / S", "Call / Appt", "Contract?", "Pre-App.",
                "Closed", "Source", "Referred By", "Status"].map((h) => (
                <th key={h} className="px-2.5 py-2 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthRows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-400">
                  No leads this month
                </td>
              </tr>
            )}
            {monthRows.map((r) => (
              <tr
                key={r.leadId}
                className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${
                  r.status === "won" ? "bg-emerald-50/60" : r.status === "lost" ? "bg-rose-50/50" : ""
                }`}
                onClick={() => openLead(r)}
              >
                <td className="px-2.5 py-1.5 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                <td className="px-2.5 py-1.5 font-semibold text-slate-800">{r.name}</td>
                <td className="px-2.5 py-1.5">{r.leadType === "buyer" ? "Buyer" : "Seller"}</td>
                <td className="px-2.5 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    title={
                      r.callOrApptOverridden
                        ? "Manually set - click to return to automatic"
                        : "Automatic from pipeline stage - click to override"
                    }
                    onClick={() => onFlipCallAppt(r)}
                    className={`rounded-md px-2 py-0.5 font-semibold ${
                      r.callOrAppt === "appt"
                        ? "bg-slate-100 text-linkColor"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {r.callOrAppt === "appt" ? "Appt" : "Call"}
                    {r.callOrApptOverridden ? " ✏️" : ""}
                  </button>
                </td>
                <td className={`px-2.5 py-1.5 ${r.contract ? "text-emerald-700 font-bold" : "text-slate-500"}`}>
                  {r.contract ? "Yes" : "No"}
                </td>
                <td className="px-2.5 py-1.5">{r.preApproved ? "Yes" : "-"}</td>
                <td className="px-2.5 py-1.5 font-bold">
                  {r.closedYear === year ? r.closedLabel : "-"}
                </td>
                <td className="px-2.5 py-1.5">
                  {r.bucket === UNASSIGNED_BUCKET ? (
                    <span
                      className="border border-dashed border-rose-300 bg-rose-50 text-rose-700 rounded px-1.5"
                      title={`"${r.sourceName || "No source"}" is not mapped to a bucket`}
                    >
                      {r.sourceName || "None"} ⚠
                    </span>
                  ) : (
                    <b>{r.bucket}</b>
                  )}
                </td>
                <td className="px-2.5 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="text-left hover:underline text-slate-700"
                    onClick={() => setPickerRow(r)}
                    title="Set who referred this client"
                  >
                    {r.referredByName || <span className="text-slate-400">Set...</span>}
                  </button>
                </td>
                <td className="px-2.5 py-1.5">
                  {r.status === "won" ? (
                    <span className="text-emerald-700 font-bold">✓ Won</span>
                  ) : r.status === "lost" ? (
                    <span className="text-rose-600 font-semibold">✕ Lost</span>
                  ) : (
                    <span className="text-blue-600 font-semibold">Active</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
        Click a row to open the lead. Click the Call/Appt chip to override it
        (✏️ means manually set). ⚠ means the lead source needs a bucket.
      </div>
      {pickerRow && (
        <ReferredByPicker
          excludeContactId={pickerRow.contactId}
          onClose={() => setPickerRow(null)}
          onDone={async (ref) => {
            const row = pickerRow;
            setPickerRow(null);
            if (!row.contactId) {
              toast.error("This lead has no linked contact");
              return;
            }
            const res = await setContactReferrer(row.contactId, ref);
            if (res.success) {
              onReferrerSaved(row.contactId, ref);
            } else {
              toast.error("Couldn't save referrer");
            }
          }}
        />
      )}
    </div>
  );
}
